import React, { useContext, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChatContext } from "../context/ChatContext";
import ChatMessage from "../components/ChatMessage";
import ChatInput from "../components/ChatInput";
import UserRegistration from "../components/UserRegistration";

const ChatPage = ({ isWidget = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [input, setInput] = useState("");

  const {
    messages,
    loading,
    activeConversation,
    loadConversation,
    createNewConversation,
    sendMessage,
    currentUser,
    registerUser,
    isCheckingUser,
  } = useContext(ChatContext);

  useEffect(() => {
    if (id === "new") {
      createNewConversation();
    } else if (id) {
      loadConversation(id);
    }
  }, [id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleBack = () => {
    navigate("/");
  };

  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion);
  };

  const handleUserRegistration = (userData) => {
    registerUser(userData);
  };

  // Show loading while checking for existing user
  if (isCheckingUser) {
    return (
      <div
        className={`${
          isWidget ? "h-full" : "min-h-screen"
        } flex items-center justify-center`}
      >
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Show registration form if no user - embedded in chat interface
  if (!currentUser) {
    return (
      <div className={`${isWidget ? "h-full" : "min-h-screen"} flex flex-col`}>
        {/* Header */}
        <div
          className={`glass border-b border-blue-500/20 ${
            isWidget ? "" : "sticky top-16"
          } z-40`}
        >
          <div
            className={`${
              isWidget ? "" : "max-w-4xl mx-auto"
            } px-3 sm:px-4 py-3`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {!isWidget && (
                  <button
                    onClick={handleBack}
                    className="p-2 rounded-lg hover:bg-white/10 transition-colors duration-300"
                  >
                    <svg
                      className="w-5 h-5 text-gray-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
                )}
                <div>
                  <h3
                    className={`${
                      isWidget ? "text-sm" : "text-lg"
                    } font-semibold text-white`}
                  >
                    Get Started
                  </h3>
                  {!isWidget && (
                    <p className="text-xs text-gray-400">
                      Quick registration to begin
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Registration Form - embedded */}
        <div className="flex-1 overflow-hidden">
          <UserRegistration
            onComplete={handleUserRegistration}
            isWidget={isWidget}
          />
        </div>
      </div>
    );
  }

  // AKCP product-specific suggestions
  const productSuggestions = [
    "What temperature sensors do you offer?",
    "Tell me about server room monitoring",
    "How does your security system work?",
    "I need help with rack monitoring",
  ];

  return (
    <div className={`${isWidget ? "h-full" : "min-h-screen"} flex flex-col`}>
      {/* Header */}
      <div
        className={`glass border-b border-blue-500/20 ${
          isWidget ? "" : "sticky top-16"
        } z-40`}
      >
        <div
          className={`${isWidget ? "" : "max-w-4xl mx-auto"} px-3 sm:px-4 py-3`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={handleBack}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors duration-300"
              >
                <svg
                  className="w-5 h-5 text-gray-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>

              <div>
                <h3
                  className={`${
                    isWidget ? "text-sm" : "text-lg"
                  } font-semibold text-white`}
                >
                  BSA Assist
                </h3>
                {!isWidget && (
                  <p className="text-xs text-gray-400">
                    Conversation #{activeConversation?.id}
                  </p>
                )}
              </div>
            </div>

            {!isWidget && (
              <div className="flex items-center space-x-1 text-xs text-gray-400">
                <svg
                  className="w-3 h-3 text-green-400"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <circle cx="12" cy="12" r="10" />
                </svg>
                <span>AI Assistant Active</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div
        ref={messagesContainerRef}
        className="flex-grow overflow-y-auto px-3 sm:px-4 py-4 sm:py-6 relative"
        style={{ paddingBottom: isWidget ? "1rem" : "2rem" }}
      >
        <div className={`${isWidget ? "" : "max-w-4xl mx-auto"}`}>
          {messages.length === 0 && !loading ? (
            <div className={`${isWidget ? "py-6" : "py-12"}`}>
              <div
                className={`glass rounded-2xl ${
                  isWidget ? "p-6" : "p-8"
                } text-center mb-6`}
              >
                <div
                  className={`${
                    isWidget ? "w-12 h-12" : "w-16 h-16"
                  } mx-auto mb-4 bg-gradient-to-br from-blue-400 to-purple-600 
                              rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/30`}
                >
                  <svg
                    className={`${isWidget ? "w-6 h-6" : "w-8 h-8"} text-white`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <h2
                  className={`${
                    isWidget ? "text-base" : "text-xl"
                  } font-semibold text-white mb-2`}
                >
                  Welcome to BSA Assist
                </h2>
                <p
                  className={`${
                    isWidget ? "text-xs" : "text-sm"
                  } text-gray-400`}
                >
                  Ask me anything about our monitoring and security solutions
                </p>
              </div>

              {/* Suggestions */}
              {!isWidget && (
                <div className="space-y-3">
                  <p className="text-xs text-gray-500 text-center uppercase tracking-wider">
                    Quick Suggestions
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                    {productSuggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="glass p-3 sm:p-4 rounded-xl hover:bg-white/5 border border-blue-500/20 
                                 hover:border-blue-400/40 transition-all duration-300 group"
                      >
                        <div className="flex items-center justify-center space-x-2">
                          <svg
                            className="w-4 h-4 text-blue-400 group-hover:text-blue-300 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                          <span
                            className="text-xs sm:text-sm font-medium bg-gradient-to-r from-blue-400 to-purple-500 
                                         bg-clip-text text-transparent group-hover:from-blue-300 group-hover:to-purple-400
                                         text-center"
                          >
                            {suggestion}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Widget Suggestions - Compact */}
              {isWidget && (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 text-center">
                    Quick Start
                  </p>
                  <div className="space-y-2">
                    {productSuggestions.slice(0, 2).map((suggestion, index) => (
                      <button
                        key={index}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="w-full glass p-2 rounded-lg hover:bg-white/5 border border-blue-500/20 
                                 hover:border-blue-400/40 transition-all duration-300 group"
                      >
                        <span
                          className="text-xs font-medium bg-gradient-to-r from-blue-400 to-purple-500 
                                     bg-clip-text text-transparent group-hover:from-blue-300 group-hover:to-purple-400
                                     block text-center"
                        >
                          {suggestion}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {messages.map((message, index) => (
                <ChatMessage
                  key={index}
                  message={message}
                  messageIndex={index}
                  conversationId={activeConversation?.id || id}
                />
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="glass rounded-2xl px-4 py-3 max-w-[80%]">
                    <div className="flex space-x-2">
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></div>
                      <div
                        className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"
                        style={{ animationDelay: "0.1s" }}
                      ></div>
                      <div
                        className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"
                        style={{ animationDelay: "0.2s" }}
                      ></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          <div ref={messagesEndRef} className="pb-4" />
        </div>
      </div>

      {/* Input Area */}
      <div className="glass-frosted border-t border-blue-500/30">
        <div
          className={`${
            isWidget ? "" : "max-w-4xl mx-auto"
          } px-3 sm:px-4 py-3 sm:py-4`}
        >
          <ChatInput input={input} setInput={setInput} />
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
